import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ROTATOR, easeOutSine, tween,
  type NavTarget, type Promo, type TenantBundle, type TweenHandle,
} from '@ce/core';
import { assetUrl, divider, kicker, t } from '../styles';

/** Taller than the source art (2.5:1), which leaves headroom for the pan. */
const ASPECT = 2.2;

/**
 * Snapping must be restored to its literal value, never to ''.
 *
 * React applies `scrollSnapType` as an inline style. Clearing it with '' deletes
 * that declaration outright, and React will not re-apply it because its virtual
 * DOM still believes the style is set — so snapping stays off for good and
 * manual swipes settle between slides.
 */
const SNAP = 'x mandatory';

/**
 * The Home rotator.
 *
 * Three motions, all driven from `@ce/core`'s shared constants so web and
 * native are paced identically:
 *
 *   1. The slide transition eases in and out — a custom rAF tween rather than
 *      `scrollTo({behavior:'smooth'})`, whose curve and duration are the
 *      browser's to choose and differ from React Native's.
 *   2. The artwork drifts and scales slowly while a slide is held. The source
 *      images are wider than the frame, so panning reveals more picture rather
 *      than exposing an edge.
 *   3. The title fades in and rises when a slide becomes active.
 *
 * The art no longer carries baked-in text, so the title is drawn by the app and
 * follows the attendee's theme — unlike the artwork itself, which stays as the
 * client shipped it.
 */
export function PromoRotator({
  bundle,
  onNavigate,
  motion = true,
}: {
  bundle: TenantBundle;
  onNavigate: (target: NavTarget) => void;
  /** False turns off the pan and the title choreography; slides still advance. */
  motion?: boolean;
}) {
  const promos = bundle.promos ?? [];
  const railRef = useRef<HTMLDivElement>(null);
  const tweenRef = useRef<TweenHandle | null>(null);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const reduceMotion = useRef(false);

  useEffect(() => {
    reduceMotion.current = !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  }, []);

  /**
   * Eased programmatic scroll.
   *
   * Scroll snapping is disabled for the duration. With `mandatory` snapping on,
   * writing `scrollLeft` every frame makes the browser fight the tween — it
   * keeps trying to re-snap mid-animation, which is what the flicker was.
   */
  const glideTo = useCallback((i: number) => {
    const rail = railRef.current;
    if (!rail) return;
    tweenRef.current?.cancel();
    const to = i * rail.clientWidth;

    if (reduceMotion.current) {
      rail.scrollLeft = to;
      return;
    }

    rail.style.scrollSnapType = 'none';
    tweenRef.current = tween(
      rail.scrollLeft, to, ROTATOR.slideMs,
      (v) => { rail.scrollLeft = v; },
      undefined,
      () => {
        rail.scrollLeft = to;
        rail.style.scrollSnapType = SNAP;
      },
    );
  }, []);

  useEffect(() => {
    if (paused || promos.length < 2 || reduceMotion.current) return;
    const id = window.setInterval(() => {
      if (document.hidden) return;
      setIndex((i) => {
        const next = (i + 1) % promos.length;
        glideTo(next);
        return next;
      });
    }, ROTATOR.holdMs);
    return () => clearInterval(id);
  }, [paused, promos.length, glideTo]);

  useEffect(() => () => {
    tweenRef.current?.cancel();
    window.clearTimeout(settleRef.current);
  }, []);

  // A manual flick wins over an in-flight tween.
  const onPointerDown = () => {
    tweenRef.current?.cancel();
    if (railRef.current) railRef.current.style.scrollSnapType = SNAP;
    setPaused(true);
  };

  const settleRef = useRef<number>(0);

  const onScroll = () => {
    const rail = railRef.current;
    if (!rail || !rail.clientWidth) return;
    const i = Math.round(rail.scrollLeft / rail.clientWidth);
    setIndex((prev) => (prev === i ? prev : i));

    // Safari and older Chrome occasionally leave a snap container a pixel or
    // two off-grid after a flick. Nudge it home once scrolling has stopped.
    window.clearTimeout(settleRef.current);
    settleRef.current = window.setTimeout(() => {
      if (!rail.clientWidth) return;
      const target = Math.round(rail.scrollLeft / rail.clientWidth) * rail.clientWidth;
      if (Math.abs(rail.scrollLeft - target) > 1) {
        rail.scrollTo({ left: target, behavior: 'smooth' });
      }
    }, 140);
  };

  if (promos.length === 0) return null;

  return (
    <div
      onPointerDown={onPointerDown}
      onFocusCapture={() => setPaused(true)}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div style={{ ...kicker(), margin: '24px 0 11px' }}>{bundle.strings.promosLabel}</div>

      <div
        ref={railRef}
        className="scroll"
        onScroll={onScroll}
        style={{
          display: 'flex',
          overflowX: 'auto',
          scrollSnapType: SNAP,
          borderRadius: 'var(--radius-lg)',
          WebkitOverflowScrolling: 'touch',
          aspectRatio: `${ASPECT}`,
          boxShadow: `0 0 0 1px ${divider}`,
        }}
      >
        {promos.map((promo, i) => {
          // Decode the neighbours ahead of time. Anything further out stays
          // lazy so a ten-card rotator does not fetch 1.5 MB on first paint.
          const near =
            Math.abs(i - index) <= 1 ||
            (index === 0 && i === promos.length - 1) ||
            (index === promos.length - 1 && i === 0);
          return (
            <PromoCard
              key={promo.id}
              promo={promo}
              active={i === index}
              motion={motion}
              eager={near}
              onSelect={() => onNavigate(promo.target)}
            />
          );
        })}
      </div>

      {promos.length > 1 && (
        <div
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: 6, marginTop: 11,
          }}
        >
          {promos.map((promo, i) => {
            const on = i === index;
            return (
              <button
                key={promo.id}
                type="button"
                aria-label={`Show ${promo.title}`}
                aria-current={on}
                onClick={() => { setIndex(i); glideTo(i); }}
                style={{
                  width: on ? 18 : 6, height: 6, borderRadius: 3, padding: 0,
                  border: 0, cursor: 'pointer',
                  background: on ? 'var(--color-accent)' : t(0.22),
                  transition: 'width .3s cubic-bezier(.65,0,.35,1), background .3s ease',
                }}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

function PromoCard({
  promo,
  active,
  motion,
  eager,
  onSelect,
}: {
  promo: Promo;
  active: boolean;
  motion: boolean;
  eager: boolean;
  onSelect: () => void;
}) {
  const [pressed, setPressed] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const panRef = useRef<TweenHandle | null>(null);
  /** How far through its pan this card is, so a reset can ease from there. */
  const progress = useRef(0);
  // Alternating drift direction keeps consecutive slides from feeling identical.
  const dir = useRef(Math.random() < 0.5 ? 1 : -1).current;
  const right = promo.textAlign === 'right';

  // Pan runs only on the active slide — ten simultaneous rAF loops would cost
  // far more than the effect is worth.
  useEffect(() => {
    const img = imgRef.current;
    if (!img) return;
    panRef.current?.cancel();

    const apply = (v: number) => {
      progress.current = v;
      const scale = ROTATOR.panScaleFrom + (ROTATOR.panScaleTo - ROTATOR.panScaleFrom) * v;
      const x = dir * ROTATOR.panDriftX * v * 100;
      const y = -ROTATOR.panDriftY * v * 100;
      img.style.transform = `scale(${scale}) translate(${x}%, ${y}%)`;
    };

    if (!motion || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      apply(0);
      return;
    }

    if (!active) {
      // Ease back to rest rather than snapping. The card is still on screen
      // while the rail moves, and a hard reset there reads as a flicker.
      if (progress.current > 0.001) {
        panRef.current = tween(progress.current, 0, ROTATOR.slideMs, apply);
      }
      return () => panRef.current?.cancel();
    }

    panRef.current = tween(0, 1, ROTATOR.panMs, apply, easeOutSine);
    return () => panRef.current?.cancel();
  }, [active, dir, motion]);

  return (
    <button
      type="button"
      onClick={onSelect}
      onPointerDown={() => setPressed(true)}
      onPointerUp={() => setPressed(false)}
      onPointerCancel={() => setPressed(false)}
      aria-label={`${promo.title}. ${promo.subtitle}. ${promo.alt}`}
      style={{
        flex: '0 0 100%',
        scrollSnapAlign: 'center',
        scrollSnapStop: 'always',
        position: 'relative',
        display: 'block',
        padding: 0,
        border: 0,
        background: 'var(--color-neutral-900)',
        cursor: 'pointer',
        overflow: 'hidden',
        transform: pressed ? 'scale(0.985)' : 'none',
        transition: 'transform .16s cubic-bezier(.2,.8,.2,1)',
      }}
    >
      <img
        ref={imgRef}
        src={assetUrl(promo.imageUrl)}
        alt=""
        loading={eager ? 'eager' : 'lazy'}
        decoding="async"
        draggable={false}
        style={{
          width: '100%', height: '100%', objectFit: 'cover', display: 'block',
          transform: `scale(${ROTATOR.panScaleFrom})`, willChange: 'transform',
        }}
      />

      {/* Scrim under the copy only. The art is the point; a full veil wastes it. */}
      <div
        style={{
          position: 'absolute', inset: 0,
          background: `linear-gradient(to ${right ? 'left' : 'right'}, rgba(var(--color-bg-rgb), 0.88) 0%, rgba(var(--color-bg-rgb), 0.55) 28%, rgba(var(--color-bg-rgb), 0.08) 56%, transparent 74%)`,
        }}
      />
      <div
        style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(to top, rgba(var(--color-bg-rgb), 0.55), transparent 46%)',
        }}
      />

      <div
        aria-hidden={!active}
        style={{
          position: 'absolute',
          left: right ? 'auto' : 18,
          right: right ? 18 : 'auto',
          bottom: 15,
          maxWidth: '68%',
          textAlign: right ? 'right' : 'left',
          willChange: 'opacity, transform',
          opacity: active || !motion ? 1 : 0,
          transform: active || !motion ? 'none' : `translateY(${ROTATOR.textRiseFrom}px)`,
          // Asymmetric on purpose: the outgoing copy leaves immediately and
          // quickly, the incoming waits for the slide to settle. Otherwise the
          // two overlap mid-transition and read as a cross-fade.
          transition: !motion
            ? 'none'
            : active
            ? `opacity ${ROTATOR.textMs}ms cubic-bezier(.2,.8,.2,1) ${ROTATOR.textInDelayMs}ms,`
              + ` transform ${ROTATOR.textMs}ms cubic-bezier(.2,.8,.2,1) ${ROTATOR.textInDelayMs}ms`
            : `opacity ${ROTATOR.textOutMs}ms ease-out, transform ${ROTATOR.textOutMs}ms ease-out`,
        }}
      >
        <div
          style={{
            font: '500 22px/1.1 var(--font-body)',
            letterSpacing: '-0.025em',
            color: 'var(--color-text)',
          }}
        >
          {promo.title}
        </div>
        <div
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 7, marginTop: 7,
            flexDirection: right ? 'row-reverse' : 'row',
          }}
        >
          <span
            style={{
              display: 'inline-grid', placeItems: 'center', width: 18, height: 18,
              borderRadius: '50%', border: '1px solid var(--color-accent)',
              color: 'var(--color-accent)', font: '500 10px/1 var(--font-body)',
            }}
          >
            ›
          </span>
          <span style={{ font: '400 12px/1.3 var(--font-body)', color: t(0.72) }}>
            {promo.subtitle}
          </span>
        </div>
      </div>
    </button>
  );
}
